import React, { useEffect, useRef } from "react"
import { graphql, StaticQuery } from "gatsby"
import Img from "gatsby-image"

import Layout from "../components/layout"
import useHoverBloom from "../components/bloom"
import SEO from "../components/seo"

// Black and white headshot. On hover, a soft circle of colour grows out from
// where the cursor entered and drifts after it, then shrinks away on leave.
const TeamPhoto = ({ fluid, alt }) => {
  const { ref, handlers } = useHoverBloom()

  return (
    <div className="team-photo" ref={ref} {...handlers}>
      <Img fluid={fluid} alt={alt} className="team-photo-base" />
      <div className="team-photo-color" aria-hidden="true">
        <Img fluid={fluid} alt="" />
      </div>
    </div>
  )
}

// Large image that starts black and white. As the page scrolls, colour spreads
// out from the middle of the image until it is fully coloured.
const ScrollBloom = ({ fluid, alt }) => {
  const box = useRef(null)
  const st = useRef({ p: 0, tp: 0, raf: null })

  const paint = () => {
    const el = box.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const { p } = st.current
    const radius = Math.hypot(rect.width, rect.height) * 0.62 * p
    el.style.setProperty("--x", `${35 + p * 30}%`)
    el.style.setProperty("--y", `${60 - p * 15}%`)
    el.style.setProperty("--r", `${Math.max(radius, 0.01)}px`)
  }

  const tick = () => {
    const s = st.current
    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    s.p += (s.tp - s.p) * (reduce ? 1 : 0.08)
    paint()
    s.raf = Math.abs(s.tp - s.p) > 0.002 ? requestAnimationFrame(tick) : null
  }

  const update = () => {
    const el = box.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const vh = window.innerHeight
    const start = vh * 0.75
    const span = start + rect.height * 0.1
    st.current.tp = Math.min(1, Math.max(0, (start - rect.top) / span))
    if (!st.current.raf) st.current.raf = requestAnimationFrame(tick)
  }

  useEffect(() => {
    const s = st.current
    update()
    window.addEventListener("scroll", update, { passive: true })
    window.addEventListener("resize", update)
    return () => {
      window.removeEventListener("scroll", update)
      window.removeEventListener("resize", update)
      cancelAnimationFrame(s.raf)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="team-photo scroll-bloom" ref={box}>
      <Img fluid={fluid} alt={alt} className="team-photo-base kg-image" />
      <div className="team-photo-color" aria-hidden="true">
        <Img fluid={fluid} alt="" />
      </div>
    </div>
  )
}

const AboutPage = ({ data }, location) => {
  const siteTitle = data.site.siteMetadata.title

  return (
    <Layout title={siteTitle}>
      <SEO
        title="About"
        keywords={[
          `ian gordon`,
          `ryan gordon`,
          `gamutsf`,
          `portland design`,
          `portland web development`,
          `email marketing`,
          `seo`,
          `aeo`,
          `geo`,
          `ai consulting`,
        ]}
      />

      <article className="post-content page-template no-image">
        <div className="post-content-body">
          <h2>
            Need it designed, built, launched and found? By All Means. We work
            alongside you to get your brand out of the idea stage and into the
            world, with a really, ridiculously close attention to detail.
          </h2>
          <figure className="kg-card kg-image-card">
            <ScrollBloom fluid={data.aboutImage.childImageSharp.fluid} alt="" />
          </figure>
          <p>
            {siteTitle} is a full service creative studio, and a small team that
            covers the whole picture: brand and design, email, social and paid
            marketing, search and AI visibility, and the websites, tools and
            automations behind them. We work closely with you to create and
            elevate your brand however, and wherever, it's experienced.
          </p>
          <div className="row">
            <div className="col-4">
              <div
                style={{
                  padding: "1rem 0",
                  textAlign: "left",
                }}
              >
                <h4>Branding</h4>
                <ul>
                  <li>Concept development</li>
                  <li>Naming</li>
                  <li>Strategy &amp; Positioning</li>
                  <li>Identity &amp; Design</li>
                  <li>Print &amp; Packaging</li>
                  <li>Brand Messaging</li>
                </ul>
              </div>
            </div>
            <div className="col-4">
              <div
                style={{
                  padding: "1rem 0",
                  textAlign: "left",
                }}
              >
                <h4>Marketing</h4>
                <ul>
                  <li>Email Marketing</li>
                  <li>Social Media</li>
                  <li>Paid Digital</li>
                  <li>Campaign Creative</li>
                  <li>Analytics &amp; Reporting</li>
                </ul>
              </div>
            </div>
            <div className="col-4">
              <div
                style={{
                  padding: "1rem 0",
                  textAlign: "left",
                }}
              >
                <h4>Search &amp; Discovery</h4>
                <ul>
                  <li>SEO</li>
                  <li>AEO &mdash; Answer Engine Optimization</li>
                  <li>AIO &mdash; AI Overviews</li>
                  <li>GEO &mdash; Generative Engine Optimization</li>
                  <li>Structured Data &amp; Schema</li>
                </ul>
              </div>
            </div>
            <div className="col-4">
              <div
                style={{
                  padding: "1rem 0",
                  textAlign: "left",
                }}
              >
                <h4>AI + Technology</h4>
                <ul>
                  <li>AI Strategy</li>
                  <li>AI Tools &amp; Workflows</li>
                  <li>AI Applications &amp; Agents</li>
                  <li>Automation</li>
                  <li>API &amp; Integrations</li>
                </ul>
              </div>
            </div>
            <div className="col-4">
              <div
                style={{
                  padding: "1rem 0",
                  textAlign: "left",
                }}
              >
                <h4>Development</h4>
                <ul>
                  <li>UI/UX</li>
                  <li>Content Strategy</li>
                  <li>Rapid Prototyping</li>
                  <li>Presentational Markup/Javascript</li>
                  <li>CMS Setup &amp; Customization</li>
                  <li>Hosting &amp; Deployment</li>
                  <li>Ecommerce</li>
                </ul>
              </div>
            </div>
            <div className="col-4">
              <div
                style={{
                  padding: "1rem 0",
                  textAlign: "left",
                }}
              >
                <h4>&amp; More...</h4>
                <ul>
                  <li>Motion Graphics</li>
                  <li>Filming &amp; Editing</li>
                  <li>Lounge-chair Psychiatry</li>
                  <li>Other stuff, probably</li>
                </ul>
              </div>
            </div>
          </div>

          <h2 style={{ marginTop: "4rem" }}>Who We Are</h2>
          <div className="row">
            <div className="col-4">
              <div style={{ padding: "1rem 0", textAlign: "left" }}>
                <TeamPhoto
                  fluid={data.ianImage.childImageSharp.fluid}
                  alt="Ian Gordon"
                />
                <h4>Ian Gordon</h4>
                <p
                  style={{ color: "var(--logoColor)", marginBottom: "1.2rem" }}
                >
                  Principal
                </p>
                <p>
                  15+ years creating iconic brands and digital experiences, from
                  strategy and identity through launch. Former Principal at{" "}
                  <a
                    href="https://gamutsf.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Gamut
                  </a>{" "}
                  in San Francisco, CA.
                </p>
              </div>
            </div>
            <div className="col-4">
              <div style={{ padding: "1rem 0", textAlign: "left" }}>
                <TeamPhoto
                  fluid={data.lynnImage.childImageSharp.fluid}
                  alt="Lynn Collette"
                />
                <h4>Lynn Collette</h4>
                <p
                  style={{ color: "var(--logoColor)", marginBottom: "1.2rem" }}
                >
                  Design Engineer &amp; Product Leader
                </p>
                <p>
                  20+ years in design engineering, prototyping and product at
                  Apple, Uber, and Automattic. Takes ideas from messy concept to
                  shipped product, and makes it happen.
                </p>
              </div>
            </div>
            <div className="col-4">
              <div style={{ padding: "1rem 0", textAlign: "left" }}>
                <TeamPhoto
                  fluid={data.ryanImage.childImageSharp.fluid}
                  alt="Ryan Gordon"
                />
                <h4>Ryan Gordon</h4>
                <p
                  style={{ color: "var(--logoColor)", marginBottom: "1.2rem" }}
                >
                  Creative Technologist
                </p>
                <p>
                  Builds the digital platforms and systems behind brands, for
                  clients from cultural institutions to global tech. Whatever
                  the stack, whatever the deadline, gets it working.
                </p>
              </div>
            </div>
          </div>
        </div>
      </article>
    </Layout>
  )
}

const indexQuery = graphql`
  query {
    site {
      siteMetadata {
        title
      }
    }
    ianImage: file(relativePath: { eq: "ian-gordon.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 600, maxHeight: 600) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    lynnImage: file(relativePath: { eq: "lynn-collette.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 600, maxHeight: 600) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    ryanImage: file(relativePath: { eq: "ryan-gordon.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 600, maxHeight: 600) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    aboutImage: file(relativePath: { eq: "sivioco-ll.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1360) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`

export default props => (
  <StaticQuery
    query={indexQuery}
    render={data => (
      <AboutPage location={props.location} data={data} {...props} />
    )}
  />
)
